import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import {
	ICmmsFilterComponent,
	ICmmsFilterEmittedValue,
} from '@app/pages/cmms/components/cmms-sort-filters-grouping/cmms-applied-filters-bar/interfaces/filter-component.interface';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { CmmsPages } from '@app/pages/cmms/state/cmms.reducer';
import { MatFormField } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { AuMatInputAutofocusDirective } from '../../../../../../shared/directives/au-mat-input-autofocus.directive';
import { NgxTolgeeModule } from '@tolgee/ngx';

export interface ICmmsCountRangeFilterValue {
	from: number;
	to: number;
}

interface PreselectedValue {
	from: number;
	to: number;
	costOfWork_Gte?: number;
	costOfWork_Lte?: number;
	costOfRepair_Gte?: number;
	costOfRepair_Lte?: number;
}

interface CountRangeFilterData {
	activePage: CmmsPages;
	columnName: string;
	preselectedValue?: {
		from: number;
		to: number;
	};
}

interface CountRangeFilterInputData extends CountRangeFilterData {
	preselectedValue: PreselectedValue;
}

@Component({
	selector: 'count-range-filter',
	templateUrl: './count-range-filter.component.html',
	styleUrls: ['./count-range-filter.component.scss'],
	imports: [
		ReactiveFormsModule,
		MatFormField,
		MatInput,
		AuMatInputAutofocusDirective,
		NgxTolgeeModule,
	],
})
export class CountRangeFilterComponent
	implements ICmmsFilterComponent, OnInit, OnDestroy
{
	@Input() set data(value: CountRangeFilterInputData) {
		const { columnName, activePage, preselectedValue } = value;

		this.configuration = {
			activePage,
			columnName,
		};

		if (preselectedValue) {
			this.configuration.preselectedValue = this.parsePreselectedValue(preselectedValue);
		}
	}

	@Output() filterValueChanged = new EventEmitter<ICmmsFilterEmittedValue>();

	formGroup: FormGroup;
	configuration: CountRangeFilterData;

	private destroy$ = new Subject<void>();

	constructor(private fb: FormBuilder) {
		this.formGroup = this.fb.group<ICmmsCountRangeFilterValue>({
			from: null,
			to: null,
		});
	}

	ngOnInit(): void {
		this.formGroup.valueChanges
			.pipe(takeUntil(this.destroy$), debounceTime(300), distinctUntilChanged())
			.subscribe(formValue => {
				this.filterValueChanged.emit({
					columnName: this.configuration.columnName,
					label: this.prepareFilterLabel(formValue),
					data: this.buildFormValue(formValue),
				});
			});

		if (this.configuration.preselectedValue) {
			const from = this.configuration.preselectedValue.from;
			const to = this.configuration.preselectedValue.to;

			this.formGroup.patchValue({ from, to });
		}
	}

	private prepareFilterLabel(formValue: ICmmsCountRangeFilterValue): string {
		const { from, to } = formValue;

		if (!from && !to) {
			return '';
		}
		if (!!from && !to) {
			return `> ${from}`;
		}
		if (!from && !!to) {
			return `< ${to}`;
		}

		return `${from} - ${to}`;
	}

	// this will lead to biiiig problems if we will add more columns with the same filter type;
	// TODO we need to refactor this to be more dynamic
	buildFormValue(formValue: ICmmsCountRangeFilterValue) {
		const columnName = this.configuration.columnName;
		let value = {};

		if (columnName === 'costOfRepair' || columnName === 'costOfWork') {
			if (formValue.from) {
				value[`${columnName}_Gte`] = formValue.from.toString();
			}

			if (formValue.to) {
				value[`${columnName}_Lte`] = formValue.to.toString();
			}
		} else {
			value = formValue;
		}

		return value;
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	private parsePreselectedValue(value: PreselectedValue): { from: number; to: number } {
		let from = 0;
		let to = 0;

		if (value.from || value.to) {
			from = value.from;
		}

		if (value.costOfRepair_Gte || value.costOfRepair_Lte) {
			from = value.costOfRepair_Gte;
			to = value.costOfRepair_Lte;
		}

		if (value.costOfWork_Gte || value.costOfWork_Lte) {
			from = value.costOfWork_Gte;
			to = value.costOfWork_Lte;
		}

		return { from, to };
	}
}
